import { LEAGUES, SUPPORTED_LEAGUE_CODES, SUPPORTED_LEAGUE_CODES_SET, resolveSupportedLeagueCode } from './leagues';

export function getDefaultLeaguePreference() {
  return SUPPORTED_LEAGUE_CODES.slice();
}

/**
 * Keep supported league codes from a stored or submitted list (order preserved, deduped).
 * @param {unknown} input
 * @returns {string[]}
 */
export function filterSupportedLeagueCodes(input) {
  if (!Array.isArray(input)) return [];

  const seen = new Set();
  const normalized = [];

  for (const raw of input) {
    const code = String(raw || '').trim().toUpperCase();
    if (!code || !SUPPORTED_LEAGUE_CODES_SET.has(code) || seen.has(code)) continue;
    seen.add(code);
    normalized.push(code);
  }

  return normalized;
}

/**
 * Normalize stored preference + denylist.
 * Visible order is `leaguePreference`; denied leagues stay hidden.
 * Supported leagues in neither list are appended to preference (new catalog leagues).
 *
 * @param {unknown} storedPreference
 * @param {unknown} storedDenied
 * @returns {{ leaguePreference: string[], leagueDenied: string[] }}
 */
export function sanitizeLeaguePreferenceState(storedPreference, storedDenied) {
  const preferenceSet = new Set();
  const leaguePreference = [];
  for (const code of filterSupportedLeagueCodes(storedPreference)) {
    preferenceSet.add(code);
    leaguePreference.push(code);
  }

  const deniedSet = new Set();
  const leagueDenied = [];
  for (const code of filterSupportedLeagueCodes(storedDenied)) {
    if (preferenceSet.has(code)) continue;
    deniedSet.add(code);
    leagueDenied.push(code);
  }

  for (const code of SUPPORTED_LEAGUE_CODES) {
    if (preferenceSet.has(code) || deniedSet.has(code)) continue;
    preferenceSet.add(code);
    leaguePreference.push(code);
  }

  if (leaguePreference.length === 0) {
    return { leaguePreference: getDefaultLeaguePreference(), leagueDenied: [] };
  }

  return { leaguePreference, leagueDenied };
}

/**
 * @param {unknown} storedPreference
 * @param {unknown} [storedDenied]
 * @returns {string[]}
 */
export function sanitizeLeaguePreference(storedPreference, storedDenied) {
  return sanitizeLeaguePreferenceState(storedPreference, storedDenied).leaguePreference;
}

/**
 * Map ordered league codes to LEAGUES objects. Empty/missing lp shows all leagues.
 * @param {string[]|null|undefined} lp
 * @param {string[]|null|undefined} [ld]
 * @returns {typeof LEAGUES}
 */
export function resolvePreferredLeagues(lp, ld) {
  const { leaguePreference: sanitized } = sanitizeLeaguePreferenceState(lp, ld);
  if (!Array.isArray(sanitized) || sanitized.length === 0) {
    return LEAGUES.slice();
  }

  const byCode = LEAGUES.reduce((acc, league) => {
    acc[league.code] = league;
    return acc;
  }, {});

  return sanitized.map((code) => byCode[String(code).toUpperCase()]).filter(Boolean);
}

/**
 * @param {string[]} codes
 * @returns {string|null} error message or null if valid
 */
/**
 * Sort items by the user's league preference order (competition code).
 * Stable tie-break preserves the original order within the same competition.
 *
 * @template T
 * @param {T[]} items
 * @param {(item: T) => unknown} getLeagueCodeValue
 * @param {string[]|null|undefined} lp
 * @param {string[]|null|undefined} [ld]
 * @returns {T[]}
 */
export function sortByLeaguePreference(items, getLeagueCodeValue, lp, ld) {
  if (!Array.isArray(items) || items.length <= 1) return items?.slice() ?? [];

  const { leaguePreference } = sanitizeLeaguePreferenceState(lp, ld);
  const rank = new Map(leaguePreference.map((code, index) => [code, index]));
  const unknownRank = leaguePreference.length;

  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const codeA = resolveSupportedLeagueCode(getLeagueCodeValue(a.item)) || '';
      const codeB = resolveSupportedLeagueCode(getLeagueCodeValue(b.item)) || '';
      const rankA = rank.has(codeA) ? rank.get(codeA) : unknownRank;
      const rankB = rank.has(codeB) ? rank.get(codeB) : unknownRank;
      if (rankA !== rankB) return rankA - rankB;
      return a.index - b.index;
    })
    .map(({ item }) => item);
}

export function validateLeaguePreferenceSelection(codes) {
  if (!Array.isArray(codes) || codes.length === 0) {
    return 'Select at least one league';
  }

  const seen = new Set();
  for (const raw of codes) {
    const code = String(raw || '').trim().toUpperCase();
    if (!code) {
      return 'Invalid league selection';
    }
    if (!SUPPORTED_LEAGUE_CODES_SET.has(code)) {
      return `Unsupported league: ${code}`;
    }
    if (seen.has(code)) {
      return `Duplicate league: ${code}`;
    }
    seen.add(code);
  }

  return null;
}
